import { useState, useMemo, Fragment } from 'react';
import {
  VEHICLE_TYPE_OPTIONS,
  vehicleTypeLabel,
  VehicleTypeIcon,
} from '@/constants/vehicleTypes';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { vehiclesApi, organizationsApi } from '@/services/api';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  TruckIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';

const vehicleSchema = z.object({
  plateNumber: z.string().min(1, 'رقم اللوحة مطلوب'),
  vehicleType: z.string().min(1, 'نوع المركبة مطلوب'),
  model: z.string().optional(),
  year: z.number().int().min(1990).max(2030).optional().nullable(),
  // Chassis / VIN — variable length, capped at 32 to match DB.
  chassisNumber: z.string().max(32, 'رقم الهيكل طويل جداً').optional().or(z.literal('')),
  // Tracker SIM phone number — free-form, capped at 25 chars.
  simNumber: z.string().max(25, 'رقم الشريحة طويل جداً').optional().or(z.literal('')),
  organizationId: z.string().uuid().optional().nullable(),
  deviceImei: z.string().length(15, 'رقم IMEI يجب أن يكون 15 رقم').optional().or(z.literal('')),
  // The vehicle's real dashboard reading. Optional: a fleet can start
  // tracking without it and record it later.
  //
  // The empty branch has to come FIRST. `z.coerce.number()` runs Number() on
  // its input, and Number('') is 0 — so with the number branch first, leaving
  // the field blank parsed as a valid 0 and was saved as an odometer reading
  // of zero, wiping whatever had been recorded before.
  initialOdometerKm: z.literal('').or(
    z.coerce.number()
      .min(0, 'لا يمكن أن تكون سالبة')
      .max(9_999_999, 'قيمة غير معقولة للمسافة المقطوعة')
  ).optional(),
  deviceModel: z.string().optional(),
});

type VehicleForm = z.infer<typeof vehicleSchema>;

const statusLabels: Record<string, string> = {
  available: 'متاحة',
  on_mission: 'في مهمة',
  maintenance: 'صيانة',
  offline: 'غير متصلة',
  emergency: 'طوارئ',
};


const vehicleTypes = VEHICLE_TYPE_OPTIONS;

export default function Vehicles() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  // Filters live in the query string so the dashboard tiles and the entities
  // page can link straight to a filtered fleet, and so the view is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Picking a vehicle here means "show me where it is" — so it opens the
   * live map already centred on that unit rather than a page the operator
   * then has to navigate away from. The map reads ?vehicle= and selects it.
   */
  const showOnMap = (vehicleId: string) => navigate(`/map?vehicle=${vehicleId}`);
  const statusFilter = searchParams.get('status') || '';
  const orgFilter = searchParams.get('organizationId') || '';
  const connectivity = searchParams.get('connectivity') || '';
  const groupByOrg = searchParams.get('group') !== 'off';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ['vehicles-with-positions', orgFilter],
    queryFn: async () => (await vehiclesApi.getWithPositions(orgFilter || undefined)).data.data,
    // Connectivity ages, so refresh it on the same cadence the map uses.
    refetchInterval: 60_000,
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await organizationsApi.getAll();
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: VehicleForm) => vehiclesApi.create({
      ...data,
      year: data.year ?? undefined,
      organizationId: data.organizationId ?? undefined,
      deviceImei: data.deviceImei || undefined,
      chassisNumber: data.chassisNumber || undefined,
      simNumber: data.simNumber || undefined,
      // Blank means "not recorded", which must not be sent as zero.
      initialOdometerKm: data.initialOdometerKm === '' || data.initialOdometerKm === undefined
        ? undefined : Number(data.initialOdometerKm),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('تم إنشاء المركبة بنجاح');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في إنشاء المركبة');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<VehicleForm> }) =>
      vehiclesApi.update(id, {
        ...data,
        year: data.year ?? undefined,
        organizationId: data.organizationId ?? undefined,
        deviceImei: data.deviceImei || undefined,
        chassisNumber: data.chassisNumber || undefined,
        simNumber: data.simNumber || undefined,
        // On edit the field is pre-filled with what is stored, so blanking it
        // is a deliberate erase — send null and let the server clear the
        // reading. (On create, blank just means "not recorded yet".)
        initialOdometerKm: data.initialOdometerKm === '' || data.initialOdometerKm === undefined
          ? null : Number(data.initialOdometerKm),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('تم تحديث المركبة بنجاح');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في تحديث المركبة');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vehiclesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('تم حذف المركبة بنجاح');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'فشل في حذف المركبة');
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VehicleForm>({
    resolver: zodResolver(vehicleSchema),
  });

  const openModal = (vehicle?: any) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      reset({
        plateNumber: vehicle.plateNumber,
        vehicleType: vehicle.vehicleType,
        model: vehicle.model || '',
        year: vehicle.year || null,
        chassisNumber: vehicle.chassisNumber || '',
        simNumber: vehicle.simNumber || '',
        organizationId: vehicle.organizationId || null,
        deviceImei: vehicle.deviceImei || '',
        deviceModel: vehicle.deviceModel || '',
        // Comes back from the API as a numeric string; the input needs a
        // number or ''. Omitting it here reset the field to blank on every
        // edit, so a saved reading looked as though it had not been stored.
        initialOdometerKm: vehicle.initialOdometerKm != null
          ? Number(vehicle.initialOdometerKm) : '',
      });
    } else {
      setEditingVehicle(null);
      reset({
        plateNumber: '',
        vehicleType: '',
        model: '',
        year: null,
        chassisNumber: '',
        simNumber: '',
        organizationId: null,
        deviceImei: '',
        deviceModel: 'FMC130',
        initialOdometerKm: '',
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVehicle(null);
    reset();
  };

  const onSubmit = (data: VehicleForm) => {
    if (editingVehicle) {
      updateMutation.mutate({ id: editingVehicle.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذه المركبة؟')) {
      deleteMutation.mutate(id);
    }
  };

  // Search hits plate number, org name, chassis VIN, SIM number, and IMEI —
  // so workshop staff can paste a VIN sticker and find the matching device.
  const filteredVehicles = useMemo(() => {
    let list: any[] = Array.isArray(vehicles) ? [...vehicles] : [];

    const q = searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter((vehicle) => (
        vehicle.plateNumber?.toLowerCase().includes(q) ||
        vehicle.organization?.nameAr?.includes(searchQuery) ||
        vehicle.chassisNumber?.toLowerCase().includes(q) ||
        vehicle.simNumber?.toLowerCase().includes(q) ||
        vehicle.deviceImei?.toLowerCase().includes(q)
      ));
    }

    if (statusFilter) list = list.filter((v) => v.status === statusFilter);
    if (connectivity === 'online') list = list.filter((v) => v.isOnline);
    else if (connectivity === 'offline') list = list.filter((v) => !v.isOnline);

    // Entity first, then plate — so a fleet reads grouped by who operates it.
    list.sort((a, b) => {
      if (groupByOrg) {
        const an = a.organization?.nameAr || a.organization?.name || 'ﻻ';
        const bn = b.organization?.nameAr || b.organization?.name || 'ﻻ';
        const byOrg = an.localeCompare(bn, 'ar');
        if (byOrg !== 0) return byOrg;
      }
      return (a.plateNumber || '').localeCompare(b.plateNumber || '', 'ar');
    });
    return list;
  }, [vehicles, searchQuery, statusFilter, connectivity, groupByOrg]);

  // Where each entity's run begins, so a heading can be injected there.
  const orgHeadingAt = useMemo(() => {
    const map = new Map<number, string>();
    if (!groupByOrg) return map;
    let last: string | null = null;
    filteredVehicles.forEach((v, i) => {
      const name = v.organization?.nameAr || v.organization?.name || 'بدون جهة';
      if (name !== last) { map.set(i, name); last = name; }
    });
    return map;
  }, [filteredVehicles, groupByOrg]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">المركبات</h1>
          <p className="text-gray-500 mt-1">إدارة أسطول سيارات الإسعاف</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          إضافة مركبة
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث عن مركبة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pr-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setParam('status', e.target.value)}
            className="input w-full sm:w-44"
          >
            <option value="">جميع الحالات</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={connectivity}
            onChange={(e) => setParam('connectivity', e.target.value)}
            className="input w-full sm:w-44"
          >
            <option value="">الاتصال: الكل</option>
            <option value="online">متصلة الآن</option>
            <option value="offline">غير متصلة</option>
          </select>
          <div className="w-full sm:w-56">
            <select
              value={orgFilter}
              onChange={(e) => setParam('organizationId', e.target.value)}
              className="input"
            >
              <option value="">جميع الجهات</option>
              {Array.isArray(organizations) && organizations.map((o: any) => (
                <option key={o.id} value={o.id}>
                  {o.nameAr || o.name}{o.vehicleCount !== undefined ? ` (${o.vehicleCount})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="px-4 pb-3 flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={groupByOrg}
              onChange={(e) => setParam('group', e.target.checked ? '' : 'off')}
              className="w-4 h-4 rounded border-gray-300 text-primary-600"
            />
            ترتيب وتجميع حسب الجهة
          </label>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-500">{filteredVehicles.length} مركبة</span>
            {(statusFilter || orgFilter || connectivity || searchQuery) && (
              <button
                onClick={() => { setSearchQuery(''); setSearchParams({}, { replace: true }); }}
                className="text-primary-600 hover:underline"
              >
                مسح الفلاتر
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table (md+) / Cards (mobile)
          Nine columns won't fit on a phone — and horizontal scroll is
          a usability dead-end. Below md we render each vehicle as a
          card with the same information laid out vertically. Above md
          we use the regular table. */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner w-8 h-8" />
          </div>
        ) : filteredVehicles?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <TruckIcon className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium">لا توجد مركبات</p>
            <p className="text-sm">قم بإضافة مركبة جديدة للبدء</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredVehicles.map((vehicle: any, i: number) => (
                <div key={vehicle.id}>
                  {orgHeadingAt.has(i) && (
                    <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100
                                    text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                      <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
                      {orgHeadingAt.get(i)}
                    </div>
                  )}
                <div
                  className="p-3 space-y-2 cursor-pointer active:bg-gray-50"
                  role="button"
                  tabIndex={0}
                  onClick={() => showOnMap(vehicle.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') showOnMap(vehicle.id); }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200
                                      flex items-center justify-center flex-shrink-0">
                        <VehicleTypeIcon type={vehicle.vehicleType} className="w-6 h-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{vehicle.plateNumber}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {vehicleTypeLabel(vehicle.vehicleType)}
                          {vehicle.model && <span> · {vehicle.model}</span>}
                        </div>
                      </div>
                    </div>
                    <span className={clsx('badge flex-shrink-0',
                      vehicle.isOnline ? 'badge-success' : 'badge-gray')}>
                      {vehicle.isOnline ? 'متصلة' : 'غير متصلة'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div className="text-gray-500">الجهة</div>
                    <div className="text-gray-900 truncate">{vehicle.organization?.nameAr || '—'}</div>

                    {vehicle.chassisNumber && (
                      <>
                        <div className="text-gray-500">رقم الهيكل</div>
                        <div className="font-mono text-gray-900 truncate" dir="ltr">{vehicle.chassisNumber}</div>
                      </>
                    )}

                    {vehicle.simNumber && (
                      <>
                        <div className="text-gray-500">الشريحة</div>
                        <div className="font-mono text-gray-900 truncate" dir="ltr">{vehicle.simNumber}</div>
                      </>
                    )}

                    {vehicle.deviceImei && (
                      <>
                        <div className="text-gray-500">IMEI</div>
                        <div className="font-mono text-gray-900 truncate" dir="ltr">{vehicle.deviceImei}</div>
                      </>
                    )}

                    <div className="text-gray-500">آخر تحديث</div>
                    <div className="text-gray-700">{dayjs(vehicle.updatedAt).format('YYYY/MM/DD HH:mm')}</div>
                  </div>

                  <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                    <Link
                      to={`/vehicles/${vehicle.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-gray-50 text-gray-700 text-sm min-h-[40px]"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                      تفاصيل
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(vehicle); }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm min-h-[40px]"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      تعديل
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(vehicle.id); }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm min-h-[40px]"
                    >
                      <TrashIcon className="w-4 h-4" />
                      حذف
                    </button>
                  </div>
                </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block table-container">
              <table className="table table-dense">
                <thead>
                  <tr>
                    <th>رقم اللوحة</th>
                    <th>النوع</th>
                    <th>الجهة</th>
                    <th>الحالة</th>
                    <th>رقم الهيكل</th>
                    <th>الشريحة</th>
                    <th>IMEI</th>
                    <th>آخر تحديث</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((vehicle: any, i: number) => (
                    <Fragment key={vehicle.id}>
                    {orgHeadingAt.has(i) && (
                      <tr className="bg-gray-50">
                        <td colSpan={9} className="py-1.5 text-xs font-semibold text-gray-600">
                          <span className="inline-flex items-center gap-1.5">
                            <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
                            {orgHeadingAt.get(i)}
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr
                      className="cursor-pointer hover:bg-primary-50/40"
                      onClick={() => showOnMap(vehicle.id)}
                      title="عرض على الخريطة"
                    >
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                            <VehicleTypeIcon type={vehicle.vehicleType} className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{vehicle.plateNumber}</div>
                            {vehicle.model && (
                              <div className="text-xs text-gray-500">{vehicle.model}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <VehicleTypeIcon type={vehicle.vehicleType} className="w-4 h-4 shrink-0" />
                          {vehicleTypeLabel(vehicle.vehicleType)}
                        </span>
                      </td>
                      <td className="whitespace-normal">
                        {vehicle.organization?.nameAr || (
                          <span className="text-gray-400">غير محدد</span>
                        )}
                      </td>
                      <td>
                        <span className={clsx('badge',
                          vehicle.isOnline ? 'badge-success' : 'badge-gray')}>
                          {vehicle.isOnline ? 'متصلة' : 'غير متصلة'}
                        </span>
                      </td>
                      <td className="whitespace-normal">
                        {vehicle.chassisNumber ? (
                          <span className="font-mono text-xs text-gray-700 break-all" dir="ltr">
                            {vehicle.chassisNumber}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="whitespace-normal">
                        {vehicle.simNumber ? (
                          <span className="font-mono text-xs text-gray-700 break-all" dir="ltr">
                            {vehicle.simNumber}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="whitespace-normal">
                        {vehicle.deviceImei ? (
                          <span className="font-mono text-xs break-all" dir="ltr">
                            {vehicle.deviceImei}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        <span className="text-xs text-gray-500 leading-tight block">
                          {dayjs(vehicle.updatedAt).format('YYYY/MM/DD')}
                          <span className="block">{dayjs(vehicle.updatedAt).format('HH:mm')}</span>
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-0.5">
                          <Link
                            to={`/vehicles/${vehicle.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="تفاصيل المركبة"
                          >
                            <InformationCircleIcon className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={(e) => { e.stopPropagation(); openModal(vehicle); }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="تعديل"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(vehicle.id); }}
                            className="p-1.5 rounded-lg hover:bg-emergency-50 text-emergency-600"
                            title="حذف"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal
          - Dialog root forced fixed + z-[2000] via inline style so it
            wins over MainLayout sidebar (z-[1500]) and isn't trapped by
            any transformed ancestor.
          - Panel: max-h-[90dvh] + overflow-y-auto so a tall form on a
            small phone (Add/Edit has 4 sections) can scroll INSIDE the
            modal instead of being clipped off-screen.
          - On phones the panel uses h-full / rounded-none style for a
            full-screen sheet feel; on tablet+ it shrinks to max-w-lg
            with rounded corners. */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        className="md:flex md:items-center md:justify-center"
        style={{ position: 'fixed', inset: 0, zIndex: 2000 }}
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4">
          <Dialog.Panel className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[95dvh] sm:max-h-[90dvh] flex flex-col">
            {/* Sticky header so the close button + title stay visible
                while the long form scrolls underneath. */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {editingVehicle ? 'تعديل المركبة' : 'إضافة مركبة جديدة'}
              </Dialog.Title>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">رقم اللوحة *</label>
                  <input
                    {...register('plateNumber')}
                    className={clsx('input', errors.plateNumber && 'input-error')}
                    placeholder="ABC-1234"
                  />
                  {errors.plateNumber && (
                    <p className="error-message">{errors.plateNumber.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">نوع المركبة *</label>
                  <select
                    {...register('vehicleType')}
                    className={clsx('input', errors.vehicleType && 'input-error')}
                  >
                    <option value="">اختر النوع</option>
                    {vehicleTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleType && (
                    <p className="error-message">{errors.vehicleType.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">الموديل</label>
                  <input
                    {...register('model')}
                    className="input"
                    placeholder="Toyota Hiace"
                  />
                </div>

                <div>
                  <label className="label">سنة الصنع</label>
                  <input
                    type="number"
                    {...register('year', { valueAsNumber: true })}
                    className="input"
                    placeholder="2024"
                    min={1990}
                    max={2030}
                  />
                </div>
              </div>

              <div>
                <label className="label">الجهة</label>
                <select
                  {...register('organizationId')}
                  className="input"
                >
                  <option value="">اختر الجهة</option>
                  {organizations?.map((org: any) => (
                    <option key={org.id} value={org.id}>
                      {org.nameAr}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">بيانات تعريف المركبة</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">رقم الهيكل</label>
                    <input
                      {...register('chassisNumber')}
                      className={clsx('input', errors.chassisNumber && 'input-error')}
                      placeholder="مثال: WDB9066331A123456"
                      maxLength={32}
                      dir="ltr"
                    />
                    {errors.chassisNumber && (
                      <p className="error-message">{errors.chassisNumber.message}</p>
                    )}
                  </div>

                  {/* Recording this pins the tracker's own counter as the
                      baseline, so the system can report the vehicle's real
                      total afterwards rather than the device's relative one. */}
                  <div>
                    <label className="label">المسافة المقطوعة الحالية (كم)</label>
                    <input
                      {...register('initialOdometerKm')}
                      type="number"
                      min={0}
                      step="1"
                      className={clsx('input', errors.initialOdometerKm && 'input-error')}
                      placeholder="قراءة عدّاد المركبة الآن"
                      dir="ltr"
                    />
                    {errors.initialOdometerKm ? (
                      <p className="error-message">{errors.initialOdometerKm.message}</p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        تُسجَّل مرة واحدة، ويحسب النظام ما بعدها من عدّاد جهاز التتبع.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="label">رقم شريحة الاتصال (SIM)</label>
                    <input
                      {...register('simNumber')}
                      className={clsx('input', errors.simNumber && 'input-error')}
                      placeholder="مثال: 218914567890"
                      maxLength={25}
                      dir="ltr"
                    />
                    {errors.simNumber && (
                      <p className="error-message">{errors.simNumber.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-medium text-gray-900 mb-3">بيانات جهاز التتبع</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">رقم IMEI</label>
                    <input
                      {...register('deviceImei')}
                      className={clsx('input', errors.deviceImei && 'input-error')}
                      placeholder="123456789012345"
                      maxLength={15}
                      dir="ltr"
                    />
                    {errors.deviceImei && (
                      <p className="error-message">{errors.deviceImei.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">موديل الجهاز</label>
                    <input
                      {...register('deviceModel')}
                      className="input"
                      placeholder="FMC130"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <div className="spinner w-5 h-5" />
                  ) : editingVehicle ? (
                    'تحديث'
                  ) : (
                    'إضافة'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary flex-1"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
